import React from 'react'
import portfolio from '../assets/portfolio-new.jpeg'
import { RevealLine, RevealWords, RevealUp } from './ui/Reveal'

// Grouped so the stack reads as short, scannable sets instead of one
// undifferentiated column of rows. Capabilities sit alongside the tools —
// "PostgreSQL" and "Database Design" say different things.
const skillGroups = [
  { label: 'Frontend', items: ['React.js', 'Next.js', 'TypeScript', 'React Router', 'Tailwind CSS', 'Framer Motion', 'Web Accessibility'] },
  { label: 'Backend', items: ['Node.js', 'Express.js', 'Nest.js', 'REST API Design', 'Authentication', 'Payment Integration', 'Webhooks', 'WebSockets', 'AI Integration', 'SSR & SEO'] },
  { label: 'Data', items: ['PostgreSQL', 'MongoDB', 'Firebase', 'Database Design'] },
  { label: 'Infrastructure', items: ['Docker', 'GitHub Actions CI/CD', 'Nginx', 'Linux VPS Hosting', 'PM2'] },
]
const education = ['Meta Web Developer']
const experience = [
  { role: 'Co-founder & CTO', company: 'intracka.com' },
  { role: 'Frontend Engineer', company: 'alabasterapp.com' },
  { role: 'Frontend Engineer', company: 'doctors.ng' },
  { role: 'Software Engineer', company: 'natureborn.africa' },
  { role: 'Software Engineer', company: 'theprofast.com' },
  { role: 'Software Engineer', company: 'kuttinedgeresources.com' },
  { role: 'Software Engineer', company: 'accessmedsupplies.com' },
]

const Label = ({ children }) => (
  <h3 className=' font-mono text-xs uppercase tracking-[0.2em] text-ink/40'>{children}</h3>
)

export const AboutSection = () => {
  return (
    <section id='about' className=' px-6 md:px-10 py-24 md:py-32 border-b border-ink/10'>
      <div className=' max-w-7xl mx-auto'>
        <div className=' grid md:grid-cols-12 gap-8 md:gap-10 items-start'>
          <div className=' md:col-span-3'>
            <p className=' font-mono text-sm text-mint-deep'>(01)</p>
            <h2 className=' font-display text-4xl md:text-5xl text-maroon mt-2 tracking-tight'>
              <RevealLine>About</RevealLine>
            </h2>
          </div>
          <div className=' md:col-span-9'>
            <p className=' font-display text-2xl md:text-4xl leading-[1.25] text-ink tracking-tight'>
              <RevealWords text='Software Engineering is where my creative and analytical sides converge. I build and ship full-stack products, from the interface down to the API, the database, and the server they run on.' />
            </p>
            <RevealUp delay={0.25}>
              <p className=' mt-8 text-base md:text-lg leading-relaxed text-ink/60 max-w-2xl'>
                Responsive layouts, smooth animation and web accessibility on the front end;
                REST APIs, database design and authentication on the back end; Docker,
                CI/CD pipelines and Nginx on a Linux VPS to get it live. I like owning a
                feature end to end, and I&apos;m always eager to learn and grow.
              </p>
            </RevealUp>
          </div>
        </div>

        <div className=' grid md:grid-cols-12 gap-10 md:gap-12 mt-16 md:mt-24 items-start'>
          <div className=' md:col-span-5'>
            <div className=' overflow-hidden rounded-2xl border border-ink/10'>
              <img
                src={portfolio}
                alt='Wilson portrait'
                className=' w-full h-full object-cover  transition-all duration-700 ease-out hover:scale-[1.03]'
              />
            </div>
          </div>

          <RevealUp className=' md:col-span-7 flex flex-col gap-9'>
            {/* Company leads, role trails as metadata — the company is the part
                that differs from row to row. */}
            <div>
              <Label>Experience</Label>
              <ul className=' mt-3 border-t border-ink/10'>
                {experience.map((item) => (
                  <li
                    key={item.company}
                    className=' group flex items-baseline justify-between gap-4 border-b border-ink/10 py-2.5'>
                    <span className=' font-display text-lg md:text-xl text-maroon tracking-tight truncate transition-transform duration-300 group-hover:md:translate-x-1.5'>
                      {item.company}
                    </span>
                    <span className=' font-mono text-[11px] uppercase tracking-[0.15em] text-ink/40 shrink-0 transition-colors duration-300 group-hover:text-mint-deep'>
                      {item.role}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <Label>Skills</Label>
              <div className=' mt-3 border-t border-ink/10'>
                {skillGroups.map((group) => (
                  <div
                    key={group.label}
                    className=' flex flex-col sm:flex-row sm:gap-6 border-b border-ink/10 py-3'>
                    <p className=' font-mono text-[11px] uppercase tracking-[0.15em] text-ink/35 sm:w-28 sm:shrink-0 sm:pt-1.5'>
                      {group.label}
                    </p>
                    <ul className=' mt-2 sm:mt-0 flex flex-wrap gap-1.5'>
                      {group.items.map((skill) => (
                        <li
                          key={skill}
                          className=' rounded-full border border-ink/15 px-3 py-1 font-mono text-[11px] text-ink/75 transition-colors duration-300 hover:border-mint-deep hover:text-mint-deep'>
                          {skill}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Education</Label>
              <ul className=' mt-3 border-t border-ink/10'>
                {education.map((item) => (
                  <li
                    key={item}
                    className=' flex items-baseline justify-between gap-4 border-b border-ink/10 py-2.5 font-display text-lg md:text-xl text-maroon tracking-tight'>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </RevealUp>
        </div>
      </div>
    </section>
  )
}
